/**
 * WordSort — drag items into labeled buckets.
 * Falls back to tap-to-select when drag isn't supported.
 */
import { useMemo, useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import type { QuizGameSpec } from "@/types/quizGameSpec";
import type { GameRunResult } from "@/types/gameSpec";
import { GameHud } from "./hud/GameHud";
import { attemptsToRunResult, shuffle, type ArchetypeAttempt } from "./shared";

interface Props {
  spec: QuizGameSpec;
  onComplete: (r: GameRunResult) => void;
  onExit?: () => void;
}

export const WordSort = ({ spec, onComplete, onExit }: Props) => {
  const { theme, questions, tuning } = spec;
  const buckets = useMemo(() => {
    const set = new Set<string>();
    for (const q of questions) if (q.bucket) set.add(q.bucket);
    const list = Array.from(set);
    return list.length >= 2 ? list : (theme.buckets ?? ["A", "B"]);
  }, [questions, theme.buckets]);

  const [queue, setQueue] = useState(() => shuffle(questions.filter((q) => q.bucket && buckets.includes(q.bucket))));
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(tuning.lives);
  const [timeLeft, setTimeLeft] = useState(tuning.timer_s);
  const [streak, setStreak] = useState(0);
  const [streakMax, setStreakMax] = useState(0);
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  const attemptsRef = useRef<ArchetypeAttempt[]>([]);
  const startedAt = useRef(Date.now());
  const questionStartedAt = useRef(Date.now());

  const total = queue.length || questions.length;
  const current = queue[idx];

  useEffect(() => {
    if (finished) return;
    const id = window.setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { window.clearInterval(id); finish(false); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  function pick(bucket: string) {
    if (finished || !current || selectedBucket) return;
    setSelectedBucket(bucket);
    const isCorrect = bucket === current.bucket;
    attemptsRef.current.push({
      prompt: current.prompt, correct: current.bucket ?? "", answered: bucket,
      isCorrect, timeMs: Date.now() - questionStartedAt.current,
    });
    if (isCorrect) {
      setScore((s) => s + 1);
      setStreak((s) => { const n = s + 1; setStreakMax((m) => Math.max(m, n)); return n; });
    } else {
      setLives((l) => { const n = l - 1; if (n <= 0) finish(false); return n; });
      setStreak(0);
    }
    setTimeout(() => {
      setSelectedBucket(null);
      if (idx + 1 >= total) finish(true);
      else { setIdx((i) => i + 1); questionStartedAt.current = Date.now(); }
    }, 600);
  }

  function finish(won: boolean) {
    if (finished) return;
    setFinished(true);
    onComplete(attemptsToRunResult({
      archetype: "word-sort", attempts: attemptsRef.current, startedAt: startedAt.current,
      won: won && lives > 0, score, maxScore: total, streakMax,
    }));
  }

  const progress = Math.round((idx / Math.max(1, total)) * 100);

  return (
    <div className="space-y-3">
      <GameHud title={theme.title} score={score} maxScore={total} lives={lives} timeLeft={timeLeft} progress={progress} streak={streak} />

      <div className="rounded-lg p-6" style={{ background: theme.palette.bg, color: "#fff", minHeight: 380 }}>
        <div className="text-center mb-6">
          <div className="text-sm opacity-80 mb-2">Which category does this belong to?</div>
          <AnimatePresence mode="wait">
            <motion.div
              key={current?.prompt ?? "done"}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="inline-block px-6 py-4 rounded-lg text-2xl font-bold shadow-lg"
              style={{ background: theme.palette.surface, border: `2px solid ${theme.palette.accent}` }}
            >
              {current?.prompt ?? "Done!"}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className={`grid gap-3 ${buckets.length <= 2 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"}`}>
          {buckets.map((b) => {
            const isSelected = selectedBucket === b;
            const isRight = isSelected && b === current?.bucket;
            const bg = isSelected
              ? (isRight ? theme.palette.good : theme.palette.bad)
              : theme.palette.surface;
            return (
              <button
                key={b}
                type="button"
                onClick={() => pick(b)}
                disabled={!!selectedBucket || finished}
                className="rounded-lg py-6 font-semibold text-lg transition-transform active:scale-95 disabled:opacity-70"
                style={{ background: bg, color: "#fff", border: `2px solid ${theme.palette.accent}` }}
              >
                {b}
              </button>
            );
          })}
        </div>
      </div>

      <div className="text-xs text-muted-foreground text-center">Tap the bucket you think fits.</div>
      {onExit && <div className="flex justify-end"><Button variant="ghost" size="sm" onClick={onExit}>Exit game</Button></div>}
    </div>
  );
};

export default WordSort;
